"use client";

import { Suspense, useState } from "react";
import { useSearchParams } from "next/navigation";
import Link from "next/link";
import { LoginButton } from "@/components/auth/login-button";
import { EmailLoginForm } from "@/components/auth/email-login-form";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { FileText, AlertCircle, Mail, CheckCircle2 } from "lucide-react";

function LoginContent() {
  const searchParams = useSearchParams();
  const error = searchParams.get("error");
  const callbackError = searchParams.get("callbackError");
  const [showEmailLogin, setShowEmailLogin] = useState(false);

  const getErrorMessage = () => {
    if (callbackError) return callbackError;
    switch (error) {
      case "OAuthCallback":
        return "Your email is not registered as a bookkeeper. If you are a client, please use the Client Portal below.";
      case "OAuthAccountNotLinked":
        return "This email is already associated with another account.";
      case "SessionExpired":
        return "Your session expired. Please sign in again.";
      case "AccessDenied":
        return "Access denied. Please contact your administrator.";
      default:
        return error ? "An error occurred during sign in. Please try again." : null;
    }
  };

  const errorMessage = getErrorMessage();

  return (
    <div className="flex min-h-screen">
      {/* Left panel - gradient with features */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-gradient-to-br from-[oklch(0.15_0.05_270)] to-[oklch(0.20_0.08_290)] text-foreground overflow-hidden">
        {/* Dot pattern overlay */}
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: "radial-gradient(circle, oklch(0.65 0.22 270 / 0.4) 1px, transparent 1px)",
            backgroundSize: "24px 24px",
          }}
        />
        <div className="relative z-10 flex flex-col justify-center px-12 xl:px-16">
          <div className="inline-flex items-center gap-3 mb-8">
            <div className="p-3 bg-primary/15 backdrop-blur glow-sm rounded-xl">
              <FileText className="h-10 w-10" />
            </div>
          </div>
          <h1 className="text-4xl xl:text-5xl font-bold leading-tight mb-4">
            Automate your bookkeeping workflow
          </h1>
          <p className="text-lg text-muted-foreground mb-10">
            AI-powered invoice processing that saves you hours every week.
          </p>
          <div className="space-y-5">
            {[
              "Extract invoice data automatically with AI",
              "Smart booking suggestions from your history",
              "Client portal for seamless document uploads",
            ].map((text) => (
              <div key={text} className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 mt-0.5 text-primary/80 shrink-0" />
                <span className="text-foreground/90">{text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right panel - login form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 bg-background">
        <div className="w-full max-w-sm">
          <div className="flex items-center mb-8">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src="/billance-ledger-logo.png"
              alt="Billance Ledger"
              className="h-14 w-auto"
            />
          </div>

          <h2 className="text-2xl font-bold mb-1">Welcome back</h2>
          <p className="text-muted-foreground mb-8">Sign in to manage invoices and bookings</p>

          {errorMessage && (
            <Alert variant="destructive" className="mb-6">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
          )}

          {showEmailLogin ? (
            <EmailLoginForm onBack={() => setShowEmailLogin(false)} />
          ) : (
            <div className="space-y-4">
              <div className="flex justify-center">
                <LoginButton />
              </div>
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <Separator className="w-full" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">or</span>
                </div>
              </div>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => setShowEmailLogin(true)}
              >
                <Mail className="mr-2 h-4 w-4" /> Login with Email
              </Button>
            </div>
          )}

          <div className="mt-8 pt-6 border-t text-center text-sm text-muted-foreground space-y-2">
            <p>This login is for bookkeepers and administrators only.</p>
            <p>
              Are you a client?{" "}
              <Link href="/client/login" className="text-primary hover:underline font-medium">
                Go to Client Portal
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function LoginPage() {
  return (
    <Suspense fallback={
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    }>
      <LoginContent />
    </Suspense>
  );
}
